/**
 * What a fan sees while a lazy route's chunk downloads.
 *
 * ONLY EVER ON A DIRECT LANDING. Route changes inside the app are React
 * transitions (v7_startTransition on the Router), and the one Suspense
 * boundary above Routes is already mounted by then, so React holds the current
 * page on screen and this never renders. It renders when somebody opens a v2
 * URL cold: a pasted link, a bookmark, a refresh. There is no previous page to
 * hold, so the boundary mounts fresh and shows its fallback.
 *
 * That case used to be `null`, which is a blank white screen for as long as
 * the chunk takes. Fine on a fast connection, and several seconds on a real
 * one, which is what /lens/universe was reported as.
 *
 * STYLED INLINE, NOT WITH UTILITIES. This renders OUTSIDE the `.v2` wrapper,
 * so it has neither the v2 token scope nor any guarantee about how Bootstrap's
 * global rules land on it. Inline styles are the one thing that behaves the
 * same in both places (see the padding notes in v2/styles/v2.css).
 */
export function RouteFallback() {
  return (
    <div
      // Not aria-busy on a live region: this is the whole page, and a screen
      // reader should hear one word about it rather than a running commentary.
      role="status"
      aria-label="Loading"
      style={{
        // Full screen, not a band. This replaces the whole page on a cold
        // landing, so anything shorter leaves the viewport part empty and part
        // spinner, which reads as a broken page rather than a loading one.
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        // The SAME grey the boot loader in index.html uses, and the same the
        // body is painted. On a cold landing the two hand off to each other:
        // the HTML one paints first, React clears #root and mounts this. Any
        // difference between them, colour or spinner geometry, shows up as a
        // flash at the handoff, which is a blink where there used to be none.
        background: "#edf2f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: "3px solid #e2e8f0",
          borderTopColor: "#94a3b8",
          // Named so it cannot collide with Bootstrap's own spinner keyframes,
          // which are global and would otherwise decide how this turns.
          animation: "v2-route-fallback-spin 700ms linear infinite",
        }}
      />
      <style>
        {`@keyframes v2-route-fallback-spin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce) {
            [aria-label="Loading"] > span { animation-duration: 2400ms; }
          }`}
      </style>
    </div>
  );
}
